/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/BusinessEventRequirements/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ReactComponent as EditIcon } from "../../assets/icons/Edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/Delete.svg";
import { ReactComponent as UploadFileIcon } from "../../assets/icons/BuyerCompanyCreation/UploadFileIcon.svg";
import { ReactComponent as DownloadFileIcon } from "../../assets/icons/BuyerCompanyCreation/DownloadFileIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/BusinessEvents/CheckCircleIcon.svg";
import { CircularProgress } from "@mui/material";
import styles from "./index.module.css";

import {
  addRequirementToParticipant,
  deleteRequirementFromParticipant,
  processRequirementsExcelFile,
} from "../../logic/businessEvents";
import {
  handleDownloadRequirementsExcelLayout,
  parseExcelFile,
} from "../../utils/common.js";
import { getActiveCategories } from "../../logic/category/index.js";

import BusinessEventRequirementCreationModal from "../BusinessEventRequirementCreation";
import ConfirmationModal from "../ConfirmationModal";
import ExcelErrorModal from "../ExcelErrorModal";

const BusinessEventRequirements = ({ open, participant, onCancel }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const creationDialogRef = useRef(null);
  const deletionDialogRef = useRef(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [requirements, setRequirements] = useState(
    participant.requerimientos || [],
  );
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [requirementError, setRequirementError] = useState("");

  // Excel handling state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelError, setExcelError] = useState("");
  const [invalidRows, setInvalidRows] = useState([]);
  const [showExcelErrorModal, setShowExcelErrorModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);

  // Fetch reference data
  const fetchReferenceData = useCallback(async () => {
    try {
      const categoriesResult = await getActiveCategories(navigate);
      setCategories(
        categoriesResult && categoriesResult.length > 0 ? categoriesResult : [],
      );
    } catch (error) {
      logger.error("Error fetching reference data:", {
        err: sanitizeError(error),
      });
      setCategories([]);
    }
  }, [navigate]);

  useEffect(() => {
    if (open) {
      fetchReferenceData();
    }
  }, [open, fetchReferenceData]);

  // Excel file handling
  const handleExcelFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        ".xlsx",
        ".xls",
      ];

      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (
        !allowedTypes.includes(file.type) &&
        !["xlsx", "xls"].includes(fileExtension)
      ) {
        setExcelError(t("pages.buyerCompanyForm.excelFileTypeError"));
        // Clear the input value to allow re-selecting the same file
        e.target.value = "";
        return;
      }

      setExcelError("");
      setInvalidRows([]);

      // Automatically process the file after selection
      await handleProcessExcelFile(file);

      // Clear the input value to allow re-selecting the same file
      e.target.value = "";
    }
  };

  const handleProcessExcelFile = async (file) => {
    logger.info("processRequirementsExcelFile", { data: file });

    setLoading(true);
    setExcelError("");
    setInvalidRows([]);

    try {
      const participantUsers = participant.ejecutivosCompradoresCompletos;

      const validationFunctions = {
        parseExcelFile,
        participantUsers,
        categories,
      };

      const result = await processRequirementsExcelFile(
        file,
        validationFunctions,
        t,
      );

      if (!result.success) {
        setExcelError(result.error);
        setInvalidRows(result.invalidRows || []);
        setShowExcelErrorModal(true);
        setLoading(false);
        return;
      }

      if (result.results && result.results.length > 0) {
        // Process valid requirements - add them to the participant
        for (const requirementData of result.results) {
          try {
            const newReq = await addRequirementToParticipant(
              participant,
              requirementData,
              navigate,
            );
            setRequirements((prev) => [
              ...prev,
              { ...newReq, puedeEliminarse: true },
            ]);
          } catch (error) {
            logger.error("Error adding requirement:", {
              err: sanitizeError(error),
            });
          }
        }

        // Show success message
        setShowSuccessMessage(true);
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setIsSlidingUp(true);
          setTimeout(() => {
            setShowSuccessMessage(false);
            setIsSlidingUp(false);
          }, 300); // Match the slideUp animation duration
        }, 3000);
      }

      if (result.errors && result.errors.length > 0) {
        setExcelError(result.error);
        setShowExcelErrorModal(true);
      }
    } catch (error) {
      logger.error("Error processing Excel file:", {
        err: sanitizeError(error),
      });
      setExcelError(t("pages.buyerCompanyForm.excelProcessingError"));
      setShowExcelErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog) {
      if (open && !showExcelErrorModal) {
        dialog.showModal();
      } else {
        dialog.close();
      }
    }
  }, [open, showExcelErrorModal]);

  useEffect(() => {
    const handleClose = () => {
      // Only call onCancel if we're not just temporarily hiding for Excel error modal
      if (!showExcelErrorModal) {
        onCancel(participant, requirements);
      }
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open && !showExcelErrorModal) {
      dialog.showModal();
    } else if (dialog && dialog.open) {
      dialog.close();
    } else {
      // Dialog already matches the desired open state
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onCancel, navigate, showExcelErrorModal]);

  const handleConfirmDelete = async () => {
    if (!selectedRequirement) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteRequirementFromParticipant(
        participant,
        selectedRequirement,
        navigate,
      );
      setRequirements((prev) =>
        prev.filter((req) => req._id !== selectedRequirement._id),
      );
    } catch (error) {
      logger.error("Error deleting participant:", {
        err: sanitizeError(error),
      });
    } finally {
      setIsDeleting(false);
      setSelectedRequirement(null);
    }
  };

  const deleteRequirement = async (requirement) => {
    logger.info("Delete requirement: ", { data: requirement });
    setSelectedRequirement(requirement);
    setDeleteConfirmationOpen(true);
  };

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRequirement(null);
    setIsDeleting(false);
  };

  const getBuyerName = (buyerId) => {
    const buyers = participant.ejecutivosCompradoresCompletos ?? [];
    const buyer = buyers.find((buyer) => buyer._id === buyerId);
    return buyer ? buyer.nombre : t("common.notAvailable");
  };

  return (
    <>
      {open && !showExcelErrorModal && <div className={styles.overlay}></div>}

      <BusinessEventRequirementCreationModal
        dialogRef={creationDialogRef}
        participant={participant}
        selectedRequirement={selectedRequirement}
        open={isCreating}
        error={requirementError}
        onCancel={() => {
          setSelectedRequirement(null);
          setRequirementError("");
          setIsCreating(false);
        }}
        onSubmit={async (newRequirement) => {
          logger.info("New Requirement: ", { data: newRequirement });
          logger.info("participant: ", { data: participant });

          // Check if newRequirement.nombre is not repeated among existing requirements
          const isDuplicateName = requirements.some((req) => {
            if (newRequirement._id) {
              return (
                req.nombre === newRequirement.nombre &&
                req._id !== newRequirement._id
              );
            }
            return req.nombre === newRequirement.nombre;
          });
          if (isDuplicateName) {
            setRequirementError("IS_NAME_DUPLICATED");
            return;
          }

          logger.info("requirements before adding requirement: ", {
            data: requirements,
          });
          const newReq = await addRequirementToParticipant(
            participant,
            newRequirement,
            navigate,
          );
          logger.info("newReq returned from addRequirementToParticipant: ", {
            data: newReq,
          });
          if (newRequirement._id) {
            // replace the old requirement in the list
            setRequirements((prev) =>
              prev.map((req) =>
                req._id === newRequirement._id
                  ? { ...newReq, puedeEliminarse: req.puedeEliminarse ?? false }
                  : req,
              ),
            );
          } else {
            setRequirements((prev) => [
              ...prev,
              { ...newReq, puedeEliminarse: true },
            ]);
          }
          setSelectedRequirement(null);
          setIsCreating(false);
          setRequirementError("");
        }}
      />

      {deleteConfirmationOpen && (
        <ConfirmationModal
          dialogRef={deletionDialogRef}
          open={deleteConfirmationOpen}
          onClose={handleCloseDeleteConfirmation}
          onConfirm={handleConfirmDelete}
          title={t("components.confirmationModal.title")}
          message={t("pages.businessEventForm.deleteRequirementWarning")}
          confirmButtonText={t("components.confirmationModal.confirm")}
          cancelButtonText={t("components.confirmationModal.cancel")}
          isLoading={isDeleting}
        />
      )}

      <dialog
        ref={dialogRef}
        className={styles.requirementsModal}
        style={{ display: showExcelErrorModal ? "none" : "block" }}
      >
        <div className={styles.closeButtonContainer}>
          <button
            className={styles.closeButton}
            onClick={() => onCancel(participant, requirements)}
          >
            &times;
          </button>
        </div>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>
            {t("components.eventRequirementsModal.title")} -{" "}
            {participant?.nombreComercial}
          </span>
          <span className={styles.modalSubtitle}>
            {t("components.eventRequirementsModal.description")}
          </span>
          <div className={styles.headerActions}>
            <div className={styles.excelSectionContainer}>
              <div className={styles.excelSectionContent}>
                <div className={styles.excelUploadSection}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelFileChange}
                    className={styles.excelFileInputHidden}
                    id="excelFileInput"
                  />
                  <div
                    className={`${styles.loadDataButton} ${
                      loading ? styles.disabled : ""
                    }`}
                    onClick={
                      !loading
                        ? () =>
                            document.getElementById("excelFileInput").click()
                        : undefined
                    }
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <>
                        <UploadFileIcon className={styles.excelButtonIcon} />
                        {t("pages.businessEventForm.loadRequirementsData")}
                      </>
                    )}
                  </div>
                </div>
                <div
                  className={styles.downloadExcelButton}
                  onClick={() => {
                    logger.info("Downloading Excel with data:", {
                      data: {
                        users:
                          participant.ejecutivosCompradoresCompletos?.length ||
                          0,
                        categories: categories?.length || 0,
                      },
                    });
                    handleDownloadRequirementsExcelLayout(
                      t,
                      participant.ejecutivosCompradoresCompletos,
                      categories,
                      participant,
                    );
                  }}
                >
                  <DownloadFileIcon className={styles.excelButtonIcon} />
                  {t("pages.buyerCompanyForm.downloadExcelLayout")}
                </div>
              </div>
            </div>
            <button
              className={styles.addRequirementButton}
              onClick={() => setIsCreating(true)}
            >
              {t("components.eventRequirementsModal.addRequirement")}
            </button>
          </div>
        </div>

        <div className={styles.requirementList}>
          {requirements.length == 0 && (
            <div className={styles.noResults}>
              <p>{t("components.eventRequirementsModal.noResults")}</p>
            </div>
          )}
          {requirements.length > 0 &&
            requirements.map((req, index) => (
              <div key={index} className={styles.requirementItem}>
                <div className={styles.requirementText}>
                  <span className={styles.requirementTitle}>{req.nombre}</span>
                  <span>
                    {t("components.eventRequirementsModal.assignedTo")}{" "}
                    {getBuyerName(req.idUsuarioComprador)}
                  </span>
                </div>
                <div className={styles.actionButtons}>
                  <div
                    className={styles.editButtonContainer}
                    onClick={() => {
                      setSelectedRequirement(req);
                      setIsCreating(true);
                    }}
                  >
                    <EditIcon className={styles.editButton} />
                  </div>
                  {req.puedeEliminarse && (
                    <div
                      className={styles.deleteButtonContainer}
                      onClick={() => deleteRequirement(req)}
                    >
                      <DeleteIcon className={styles.deleteButton} />
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </dialog>

      {/* Excel Error Modal */}
      <ExcelErrorModal
        open={showExcelErrorModal}
        onClose={() => setShowExcelErrorModal(false)}
        errorMessage={excelError}
        invalidRows={invalidRows}
      />

      {/* Success Message */}
      {showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${
            isSlidingUp ? styles.slideUp : ""
          }`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {t("pages.buyerCompanyForm.excelFileSuccessMessage")}
        </div>
      )}
    </>
  );
};

export default BusinessEventRequirements;
